import { Scene } from 'phaser';
import { Unit } from '../entities/Unit';
import { UnitType } from '../config/BalanceConfig';

export class ProgressionSystem {
  private scene: Scene;
  private unitSpawnInterval: number = 3000;
  private availableUnits: number = 0;
  private storedChargers: number = 0;
  private maxStoredChargers: number = 5;
  private unitSpawnTimer: Phaser.Time.TimerEvent;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  public start(): void {
    this.unitSpawnTimer = this.scene.time.addEvent({
      delay: this.unitSpawnInterval,
      callback: () => this.onUnitAvailable(),
      repeat: -1
    });
  }

  public stop(): void {
    if (this.unitSpawnTimer) {
      this.unitSpawnTimer.remove();
    }
  }

  public getAvailableUnits(): number {
    return this.availableUnits;
  }

  public hasAvailableUnit(): boolean {
    return this.availableUnits > 0;
  }

  public consumeUnit(): boolean {
    if (this.availableUnits > 0) {
      this.availableUnits--;
      this.scene.events.emit('unit-consumed', this.availableUnits);
      return true;
    }
    return false;
  }

  public storeCharger(): boolean {
    if (this.storedChargers < this.maxStoredChargers) {
      this.storedChargers++;
      this.scene.events.emit('charger-stored', this.storedChargers);
      return true;
    }
    return false;
  }

  public getStoredChargers(): number {
    return this.storedChargers;
  }

  public releaseChargers(): number {
    const releasedCount = this.storedChargers;
    this.storedChargers = 0;
    this.scene.events.emit('chargers-released', releasedCount);
    return releasedCount;
  }

  public canStoreCharger(): boolean {
    return this.storedChargers < this.maxStoredChargers;
  }

  public hasStoredChargers(): boolean {
    return this.storedChargers > 0;
  }

  private onUnitAvailable(): void {
    this.availableUnits++;
    this.scene.events.emit('unit-available', this.availableUnits);
  }

  public assignUnitToRole(unitType: UnitType): Unit | null {
    if (!this.consumeUnit()) {
      return null;
    }

    const { width, height } = this.scene.cameras.main;
    const x = Phaser.Math.Between(50, width - 50);
    const y = height - 100;

    const unit = new Unit(this.scene, x, y, unitType);
    
    // Special handling for chargers
    if (unitType === UnitType.CHARGER) {
      // Player can choose to store charger instead of deploying
      this.scene.events.emit('charger-option-available', unit);
    }

    this.scene.events.emit('unit-deployed', unit);
    return unit;
  }

  public getUnitSpawnInterval(): number {
    return this.unitSpawnInterval;
  }

  public setUnitSpawnInterval(interval: number): void {
    this.unitSpawnInterval = interval;
    this.stop();
    this.start();
  }

  public upgradeSpawnRate(): void {
    // Reduce spawn interval by 10% (minimum 1 second)
    const newInterval = Math.max(1000, this.unitSpawnInterval * 0.9);
    this.setUnitSpawnInterval(newInterval);
  }

  public upgradeMaxChargers(): void {
    this.maxStoredChargers++;
    this.scene.events.emit('max-chargers-upgraded', this.maxStoredChargers);
  }

  public getMaxStoredChargers(): number {
    return this.maxStoredChargers;
  }
}